import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faEnvelope, faEye } from "@fortawesome/free-solid-svg-icons";
import "../css/DocumentsPage.css";
import "../css/EmsDocumentsDownloads.css";
import "../css/ParticipantList.css";
import { downloadEsignPdf, previewEsignPdf } from "../../utils/esignDownloadUrl";
import { GoldLoader } from "../shared/Loaders";
import DocumentStatusPills from "../shared/DocumentStatusPills";

const API_URL = process.env.REACT_APP_API_URL;

const EmsDocumentsDownloads = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [notesDirty, setNotesDirty] = useState({});
  const [sendingReminder, setSendingReminder] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [previewingId, setPreviewingId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchList = useCallback(async () => {
    if (!API_URL) {
      setFetchError("API URL is not configured (REACT_APP_API_URL).");
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setFetchError(null);
      const res = await axios.get(`${API_URL}/esign-documents/list`, {
        params: { _t: Date.now() },
        headers: { "Cache-Control": "no-cache" },
      });
      const raw = res.data;
      const arr =
        Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.data)
            ? raw.data
            : Array.isArray(raw?.list)
              ? raw.list
              : Array.isArray(raw?.documents)
                ? raw.documents
                : [];
      const str = (v) => (v != null && v !== "" ? String(v).trim() : "");
      setList(
        arr.map((row) => {
          const person = row.personId && typeof row.personId === "object" ? row.personId : null;
          return {
            _id: row._id,
            personId: row.personId?._id ?? row.personId,
            participantId: row.participantId?._id ?? row.participantId ?? null,
            firstName: str(row.firstName ?? row.first_name ?? person?.name),
            lastName: str(row.lastName ?? row.last_name ?? person?.lname),
            email: str(row.email ?? person?.email),
            doc_name: str(row.doc_name ?? row.title) || "IGF Registration Regulations",
            status: row.status ?? "NOT_SIGNED",
            signers: Array.isArray(row.signers) ? row.signers : [],
            isMinor: row.isMinor === true,
            isCaddie: row.isCaddie === true,
            signingUrl: row.signingUrl ?? row.signing_url ?? "",
            downloadUrl: row.downloadUrl ?? row.download_url ?? "",
            legalesignDocId: row.legalesignDocId ?? "",
            notes: row.notes ?? "",
          };
        })
      );
    } catch (err) {
      const message =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        "Failed to load documents.";
      setFetchError(message);
      setList([]);
      console.error("Failed to fetch document list:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleNotesChange = (docId, value) => {
    setNotesDirty((prev) => ({ ...prev, [docId]: value }));
  };

  const saveNotes = async (docId) => {
    const value = notesDirty[docId];
    if (value === undefined) return;
    try {
      await axios.patch(`${API_URL}/esign-documents/${docId}/notes`, { notes: value });
      setList((prev) =>
        prev.map((row) => (row._id === docId ? { ...row, notes: value } : row))
      );
      setNotesDirty((prev) => {
        const next = { ...prev };
        delete next[docId];
        return next;
      });
    } catch (err) {
      console.error("Failed to save notes:", err);
    }
  };

  const handleSendReminder = async (docId) => {
    try {
      setSendingReminder(docId);
      await axios.post(`${API_URL}/esign-documents/${docId}/send-reminder`);
      alert("Reminder email sent.");
    } catch (err) {
      alert(err.response?.data?.error || "Failed to send reminder.");
    } finally {
      setSendingReminder(null);
    }
  };

  const handleDownload = async (legalesignDocId) => {
    setDownloadingId(legalesignDocId);
    try {
      await downloadEsignPdf(legalesignDocId);
    } catch (err) {
      alert(err.response?.data?.error || err.message || "Download failed.");
    } finally {
      setDownloadingId(null);
    }
  };

  /** Admin views the participant's current (unsigned) Legalesign PDF — does not sign or regenerate. */
  const handlePreview = async (row) => {
    if (!row.legalesignDocId) return alert("No document generated yet for this participant.");
    setPreviewingId(row._id);
    try {
      await previewEsignPdf(row.legalesignDocId);
    } catch (err) {
      alert(err.message || "Preview failed.");
    } finally {
      setPreviewingId(null);
    }
  };

  if (loading) {
    return <GoldLoader label="Loading..." variant="page" />;
  }

  const display = (val) => (val != null && String(val).trim() !== "" ? String(val).trim() : "—");

  // Filter list by search term
  const filteredList = list.filter((doc) =>
    (doc.firstName || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (doc.lastName || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (doc.email || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (doc.doc_name || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination calculations
  const effectivePerPage = itemsPerPage >= filteredList.length ? Math.max(filteredList.length, 1) : itemsPerPage;
  const totalPages = Math.max(Math.ceil(filteredList.length / effectivePerPage), 1);
  const indexOfLast = currentPage * effectivePerPage;
  const indexOfFirst = indexOfLast - effectivePerPage;
  const currentItems = filteredList.slice(indexOfFirst, indexOfLast);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  // Get visible page numbers (show 5 pages around current)
  const getVisiblePages = () => {
    if (totalPages <= 5) return pageNumbers;
    if (currentPage <= 3) return pageNumbers.slice(0, 5);
    if (currentPage >= totalPages - 2) return pageNumbers.slice(-5);
    return pageNumbers.slice(currentPage - 3, currentPage + 2);
  };

  return (
    <div className="ems-docs-downloads p-6">
      {fetchError && (
        <div className="alert alert-warning mb-3 d-flex align-items-center justify-content-between">
          <span>{fetchError}</span>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={fetchList}>
            Retry
          </button>
        </div>
      )}
      {/* Header with Search on left */}
      <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2 ems-list-toolbar">
        <div className="d-flex align-items-center w-25">
          <div className="me-2" style={{ flex: "1" }}>
            <input
              type="text"
              className="form-control form-control-sm search-input"
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              title="Search documents"
              style={{ border: "1px solid #d1cfcf" }}
            />
          </div>
        </div>
      </div>
        <div className="ems-docs-header">
          <div>First name</div>
          <div>Last name</div>
          <div>Email</div>
          <div>Doc name</div>
          <div>Status</div>
          <div>Notes</div>
          <div>Actions</div>
        </div>
        {filteredList.length === 0 ? (
          <div className="ems-docs-empty p-4 text-center">
            <p className="mb-2">{searchTerm ? "No documents match your search." : "No documents found."}</p>
            {!searchTerm && (
              <p className="small text-muted mb-3">
                Documents appear here after participants open the Documents page (user dashboard) and generate or sign their registration document.
              </p>
            )}
            <button type="button" className="btn btn-sm btn-outline-primary" onClick={fetchList}>
              Refresh list
            </button>
          </div>
        ) : (
          currentItems.map((row) => {
            const isSigned = (row.status || "").toUpperCase() === "SIGNED";
            const docName = (row.doc_name && String(row.doc_name).trim()) ? String(row.doc_name).trim() : "IGF Registration Regulations";
            const notesValue = notesDirty[row._id] !== undefined ? notesDirty[row._id] : (row.notes ?? "");
            return (
              <div key={row._id} className="ems-docs-row">
                <div>{display(row.firstName)}</div>
                <div>{display(row.lastName)}</div>
                <div>{display(row.email)}</div>
                <div>{docName}</div>
                <div>
                  <DocumentStatusPills
                    status={row.status}
                    signers={row.signers}
                    isMinor={row.isMinor}
                    role={row.isCaddie ? "Caddie" : "Athlete"}
                  />
                </div>
                <div className="ems-docs-notes">
                  <textarea
                    className="ems-notes-textarea"
                    value={notesValue || ""}
                    onChange={(e) => handleNotesChange(row._id, e.target.value)}
                    onBlur={() => saveNotes(row._id)}
                    placeholder="Notes"
                    rows={1}
                  />
                </div>
                <div className="ems-docs-actions">
                  {isSigned ? (
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() => handleDownload(row.legalesignDocId)}
                      disabled={downloadingId === row.legalesignDocId}
                      style={{
                        background: "#D9A128",
                        color: "#fff",
                        border: "none",
                        padding: "3px 10px",
                        borderRadius: "4px",
                        fontWeight: 600,
                        fontSize: "12px",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        cursor: downloadingId === row.legalesignDocId ? "not-allowed" : "pointer",
                      }}
                    >
                      <FontAwesomeIcon icon={faDownload} />{" "}
                      {downloadingId === row.legalesignDocId ? "Downloading..." : "Download"}
                    </button>
                  ) : (
                    <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => handlePreview(row)}
                        disabled={previewingId === row._id}
                        style={{
                          background: "#D9A128",
                          color: "#fff",
                          border: "none",
                          padding: "3px 10px",
                          borderRadius: "4px",
                          fontWeight: 600,
                          fontSize: "12px",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          whiteSpace: "nowrap",
                          cursor: previewingId === row._id ? "not-allowed" : "pointer",
                          opacity: previewingId === row._id ? 0.6 : 1,
                        }}
                      >
                        <FontAwesomeIcon icon={faEye} />{" "}
                        {previewingId === row._id ? "Opening..." : "Preview Document"}
                      </button>
                      <button
                        type="button"
                        className="btn-icon"
                        onClick={() => handleSendReminder(row._id)}
                        disabled={sendingReminder === row._id}
                        title="Send reminder email"
                        style={{
                          background: "transparent",
                          border: "none",
                          padding: "4px 8px",
                          cursor: "pointer",
                          color: "#555",
                        }}
                      >
                        <FontAwesomeIcon icon={faEnvelope} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}

        {/* Pagination inside table */}
        {filteredList.length > 0 && (
          <div className="participant-pagination">
            <div className="d-flex align-items-center gap-2">
              <span style={{ fontSize: "13px", color: "#666" }}>Show</span>
              <select
                className="form-select form-select-sm"
                style={{ width: "auto", fontSize: "13px" }}
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>

            <div className="pagination-box">
              <button
                className="pagination-btn"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                Previous
              </button>

              {getVisiblePages().map((num) => (
                <button
                  key={num}
                  className={`pagination-btn ${num === currentPage ? "active" : ""}`}
                  onClick={() => setCurrentPage(num)}
                >
                  {num}
                </button>
              ))}

              <button
                className="pagination-btn"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
              </button>
            </div>
          </div>
        )}
    </div>
  );
};

export default EmsDocumentsDownloads;
